// @ts-strict
import cx from "classnames";
import { ReactNode } from "react";

import { Button } from "./Button";
import { Icon } from "./Icon";

export type AlertVariant = "info" | "success" | "warning" | "error";
export type AlertPadding = "default" | "medium" | "large";

interface AlertProps {
  /** Visual + semantic variant. Defaults to "info". */
  variant?: AlertVariant;
  /** Optional bold heading shown above the message. */
  title?: string;
  /** Message content. */
  children: ReactNode;
  className?: string;
  /** Controls container padding without conflicting with the default spacing. */
  padding?: AlertPadding;
  /** Overrides the variant's default icon (e.g. "shieldAlert" for security). */
  icon?: string;
  /** When provided, renders a dismiss (×) button that calls this. */
  onDismiss?: () => void;
  /** Keeps the dismiss button visible but prevents interaction. */
  dismissDisabled?: boolean;
}

// Colors use the tokens most commonly used for these message types across the
// portal (bg-blue-50/text-blue-300 for info, bg-green-50/text-teal-400 for
// success, etc.); icons come from the shared Feather icon set.
const variantConfig: Record<
  AlertVariant,
  { container: string; accent: string; icon: string }
> = {
  info: {
    container: "bg-blue-50 border-blue-300/30",
    accent: "text-blue-300",
    icon: "info"
  },
  success: {
    container: "bg-green-50 border-teal-400/30",
    accent: "text-teal-400",
    icon: "checkCircle"
  },
  warning: {
    container: "bg-yellow-50 border-yellow-400/30",
    accent: "text-yellow-400",
    icon: "warning"
  },
  error: {
    container: "bg-error-xlight border-error/30",
    accent: "text-error",
    icon: "alertCircle"
  }
};

const paddingClasses: Record<AlertPadding, string> = {
  default: "px-3 py-2",
  medium: "p-4",
  large: "p-5"
};

/**
 * Inline status message (info / success / warning / error). For a transient
 * pop-up notification use Toast instead.
 */
const Alert = ({
  variant = "info",
  title,
  children,
  className,
  padding = "default",
  icon,
  onDismiss,
  dismissDisabled = false
}: AlertProps) => {
  const { container, accent, icon: defaultIcon } = variantConfig[variant];
  const iconName = icon ?? defaultIcon;

  return (
    <div
      role={variant === "error" || variant === "warning" ? "alert" : "status"}
      className={cx(
        "flex items-start gap-2 rounded border text-sm text-gray-700 [&_a]:text-blue-300 [&_a]:font-semibold [&_a]:cursor-pointer [&_a:hover]:underline",
        paddingClasses[padding],
        container,
        className
      )}
    >
      <Icon name={iconName} size="small" className={cx("mt-0.5 flex-none", accent)} />
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold text-gray-900">{title}</p>}
        <div>{children}</div>
      </div>
      {onDismiss && (
        <Button
          kind="close-icon"
          onClick={onDismiss}
          disabled={dismissDisabled}
          aria-label="Dismiss"
          className="!m-0 !p-0 flex-none text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:hover:text-gray-400"
        >
          <Icon name="close" size="small" />
        </Button>
      )}
    </div>
  );
};

export default Alert;
